import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { FolderOrDocument } from "certiblok-api-manager";
import { nanoid } from "nanoid";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { subscribe, useSnapshot } from "valtio";
import MLDialog from "../../../components/poppers";
import { apiErrorParser } from "../../../hooks/api";
import { useTreeViewContext } from "../TreeViewController";
import { PER_PAGE } from "../constants/treeViewConstants";
import { computeNextFolderOnSameLevelIndex } from "../functions/treeViewFunctions";
import { treeViewQueryKeys } from "../queries/treeViewQueries";
import type { TreeViewListItem } from "../types/ListItem";
import type { TreeViewOpenedFolder } from "../types/OpenedFolder";
import { throttle } from "lodash";
import { useSearchParams } from "react-router-dom";

export const useTreeViewItemsList = () => {
	const { itemsList } = useTreeViewContext();
	return itemsList;
};

export function useInitTreeView() {
	const { itemsList, fetchRootLayer, openedFolders, rootFolderId, appliedFilters } = useTreeViewContext();

	const queryClient = useQueryClient();

	const { data, isLoading } = useQuery({
		queryKey: treeViewQueryKeys.root(rootFolderId, { page: 0, perPage: PER_PAGE }, appliedFilters),
		queryFn: () => {
			return fetchRootLayer({ page: 0, perPage: PER_PAGE });
		},
		onError: (error) => {
			MLDialog.showSnackbar(apiErrorParser(error), { variant: "error" });
		},
		select: (data) => {
			return data;
		},
		keepPreviousData: true,
	});

	useEffect(() => {
		let totalItems = parseInt(data?.headers["x-total-count"] ?? "0");
		if (isNaN(totalItems)) {
			totalItems = 0;
		}
		const hasAlreadyFetched = openedFolders.has(rootFolderId);
		if (hasAlreadyFetched) {
			const rootParentFolder = openedFolders.get(rootFolderId)!;
			if (totalItems > rootParentFolder.totalItems) {
				itemsList.push(
					...Array.from(Array(totalItems - rootParentFolder.totalItems)).map<TreeViewListItem>(() => {
						return {
							parentFolder: rootParentFolder,
							isLoading: true,
							id: nanoid(),
						};
					})
				);
				queryClient.invalidateQueries(treeViewQueryKeys.folder.base());
			} else if (totalItems < rootParentFolder.totalItems) {
				const totalItemsToRemove = rootParentFolder.totalItems - totalItems;
				itemsList.splice(itemsList.length - totalItemsToRemove, totalItemsToRemove);
				queryClient.invalidateQueries(treeViewQueryKeys.folder.base());
			}
			rootParentFolder.totalItems = totalItems;
			return;
		}
		const parentFolder: TreeViewOpenedFolder = {
			folderId: rootFolderId,
			deepness: 0,
			isExpanded: true,
			totalItems,
			id: rootFolderId,
		};
		openedFolders.set(rootFolderId, parentFolder);
		itemsList.push(
			...(data?.data ?? []).map<TreeViewListItem>((elem) => {
				return {
					parentFolder,
					item: elem,
					isLoading: false,
					id: elem.id,
				};
			}),
			...Array.from(Array(totalItems - (data?.data ?? []).length)).map<TreeViewListItem>(() => {
				return {
					parentFolder,
					isLoading: true,
					id: nanoid(),
				};
			})
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	return { isLoading };
}

export function useHandleScroll() {
	const { ignoreQueryParams } = useTreeViewContext();
	const hasScrolled = useRef(false);
	const listRef = useRef<HTMLDivElement>();
	const [, setSearchParams] = useSearchParams();
	const userScrolledUp = useRef(false);
	const restoreAttempts = useRef(0);

	const restoreScroll = useCallback((element: HTMLDivElement) => {
		if (!listRef.current) {
			listRef.current = element;
		}

		// Don't restore if user has manually scrolled up
		if (userScrolledUp.current) {
			hasScrolled.current = true;
			return;
		}

		// // Don't restore if we've exceeded max attempts
		// if (restoreAttempts.current >= MAX_RESTORE_ATTEMPTS) {
		// 	hasScrolled.current = true;
		// 	return;
		// }

		if (!hasScrolled.current && element && !ignoreQueryParams) {
			const urlParams = new URLSearchParams(window.location.search);
			const scrollPosition = urlParams.get("scrollPosition") ?? "0";
			const currentScroll = element.scrollTop;
			const targetScroll = parseInt(scrollPosition);

			const handleUserScroll = (e: WheelEvent) => {
				if (e.deltaY < 0) {
					// User is scrolling up
					userScrolledUp.current = true;
				}
			};

			// Detect if user is trying to scroll up
			element.addEventListener("wheel", handleUserScroll, { once: true });

			if (currentScroll >= targetScroll) {
				hasScrolled.current = true;
				return;
			} else {
				element.scrollTo({ top: targetScroll });
				restoreAttempts.current++;
				setTimeout(() => {
					restoreScroll(element);
				}, 300);
			}
		}
	}, []);

	const keepScroll = useMemo(() => {
		return throttle(
			(element?: HTMLDivElement) => {
				if (hasScrolled.current && !ignoreQueryParams) {
					const searchParams = new URLSearchParams(window.location.search);
					searchParams.set("scrollPosition", (element?.scrollTop ?? 0).toString());
					setSearchParams(searchParams, {
						replace: true,
					});
				}
			},
			500,
			{
				leading: true,
				trailing: true,
			}
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return { listRef, restoreScroll, keepScroll };
}

export function useRemoveTreeViewItems() {
	const { itemsList, openedFolders } = useTreeViewContext();
	const queryClient = useQueryClient();

	return useCallback(
		(itemIds: string[]) => {
			itemIds.forEach((toRemoveId) => {
				const indexInList = itemsList.findIndex((item) => item.item?.id === toRemoveId);
				if (indexInList === -1) return;
				const item = itemsList[indexInList];
				if (item.item?.isFolder) {
					const folder = openedFolders.get(item.id);
					if (folder) {
						openedFolders.delete(item.id);
					}
					itemsList.splice(indexInList, computeNextFolderOnSameLevelIndex(indexInList, itemsList) - indexInList);
				} else {
					itemsList.splice(indexInList, 1);
				}
				item.parentFolder.totalItems--;
			});
			queryClient.invalidateQueries(treeViewQueryKeys.base);
		},
		[itemsList, openedFolders, queryClient]
	);
}

export function useToggleSelectionMode() {
	// const { isSelecting } = useTreeViewContext();

	return useCallback(() => {
		// isSelecting.value = !isSelecting.value;
	}, []);
}

export function useIsSelecting() {
	const { isSelecting } = useTreeViewContext();
	return useSnapshot(isSelecting).value;
}

export function useAreItemsSelected() {
	const { selectedItemIds } = useTreeViewContext();
	const [areItemsSelected, setAreItemsSelected] = useState(selectedItemIds.values.length > 0);

	useEffect(() => {
		return subscribe(selectedItemIds, () => {
			setAreItemsSelected(selectedItemIds.values.length > 0);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return areItemsSelected;
}

export function useIsItemSelected(id: string) {
	const { selectedItemIds } = useTreeViewContext();
	const [isSelected, setIsSelected] = useState(selectedItemIds.values.includes(id));

	useEffect(() => {
		return subscribe(selectedItemIds, () => {
			setIsSelected(selectedItemIds.values.includes(id));
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	return isSelected;
}

export function useToggleSelectItems() {
	const { selectedItemIds } = useTreeViewContext();

	return useCallback(
		({ itemIds, shouldReplace }: { itemIds: string[]; shouldReplace?: boolean }) => {
			if (shouldReplace) selectedItemIds.values = [];
			itemIds.forEach((id) => {
				if (selectedItemIds.values.includes(id)) {
					selectedItemIds.values.splice(selectedItemIds.values.indexOf(id), 1);
				} else {
					selectedItemIds.values.push(id);
				}
			});
		},
		[selectedItemIds]
	);
}

export function getItemsByIds(itemsList: TreeViewListItem[], selectedItemIds: readonly string[]) {
	const res: TreeViewListItem[] = [];
	itemsList.forEach((item) => {
		if (selectedItemIds.includes(item.item?.id ?? "")) res.push(item);
	});
	return res;
}

export function useSelectedItems() {
	const { selectedItemIds, itemsList } = useTreeViewContext();
	const [selectedItems, setSelectedItems] = useState<FolderOrDocument[]>([]);
	useEffect(
		() => {
			const onSelectedItemsChange = () => {
				const selectedItems = getItemsByIds(itemsList, selectedItemIds.values);
				setSelectedItems(selectedItems.map((item) => item.item!));
			};
			return subscribe(selectedItemIds, onSelectedItemsChange);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	return selectedItems;
}

export function useRemoveNonRootItems() {
	const { itemsList, openedFolders } = useTreeViewContext();

	return useCallback(() => {
		for (let i = itemsList.length - 1; i >= 0; i--) {
			const item = itemsList[i];
			if (item.parentFolder.deepness !== 0) {
				if (openedFolders.has(item.parentFolder.id)) {
					openedFolders.delete(item.parentFolder.id);
				}
				if (item.parentFolder.isExpanded) {
					item.parentFolder.isExpanded = false;
				}
				itemsList.splice(i, 1);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
}
